import React, { useEffect, useState } from 'react';
import { Trophy, Zap, ArrowUp } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { XPNotification } from '../types';

interface Props {
    notification: XPNotification | null;
    onClose: () => void;
}

export const XPToast: React.FC<Props> = ({ notification, onClose }) => {
    const { theme } = useTheme();
    const [isVisible, setIsVisible] = useState(false);
    
    // Persist data for exit animation
    const [displayData, setDisplayData] = useState<XPNotification | null>(null);

    useEffect(() => {
        if (notification) {
            setDisplayData(notification);
            setIsVisible(true);
            
            const timer = setTimeout(() => {
                setIsVisible(false);
                setTimeout(onClose, 500); // Wait for exit animation
            }, 4000);
            
            return () => clearTimeout(timer);
        }
    }, [notification, onClose]);

    // Use current notification if available, otherwise fallback to last known (for exit animation)
    const activeData = notification || displayData;

    if (!activeData && !isVisible) return null;

    const isLevelUp = !!activeData?.newLevel;

    return (
        <div className={`fixed top-24 right-4 z-[60] transition-all duration-500 transform ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}>
            <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-md p-4 min-w-[280px] ${
                isLevelUp 
                ? 'bg-gradient-to-br from-amber-500 to-orange-600 border-amber-300 text-white' 
                : theme.mode === 'dark' ? 'bg-slate-800/90 border-indigo-500/30 text-white' : 'bg-white/90 border-indigo-100 text-slate-800'
            }`}>
                
                {/* Shine Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-[shimmer_2s_infinite]"></div>

                <div className="flex items-center gap-4 relative z-10">
                    <div className={`p-3 rounded-full shrink-0 ${isLevelUp ? 'bg-white/20' : 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400'}`}>
                        {isLevelUp ? <Trophy size={24} className="animate-bounce" /> : <Zap size={24} className="animate-pulse" />}
                    </div>
                    
                    <div className="flex-1">
                        <h4 className="font-black text-lg leading-none">
                            {isLevelUp ? `LEVEL ${activeData?.newLevel}!` : `+${activeData?.amount} XP`}
                        </h4>
                        <p className={`text-xs font-medium mt-1 ${isLevelUp ? 'text-amber-100' : 'opacity-60'}`}>
                            {isLevelUp ? 'You reached a new tier!' : activeData?.reason || 'Insight Gained'}
                        </p>
                    </div>

                    {isLevelUp && (
                        <ArrowUp size={24} className="text-white/50 animate-ping" />
                    )}
                </div>

                {/* Progress Bar (Fake for visual satisfaction) */}
                <div className="mt-3 h-1.5 w-full bg-black/10 rounded-full overflow-hidden">
                    <div className="h-full bg-white/80 w-full animate-[grow_1s_ease-out_forwards] origin-left"></div>
                </div>
            </div>
        </div>
    );
};